import type { TenantTheme } from "@kovernow/types/tenant-web-config";
import { composite, contrastRatio, mix, parseColor, type Rgba } from "./color.js";

/**
 * WCAG AA contrast gate for a tenant theme (CAP-2, CAP-7, ux-foundations §1).
 * Text pairs need 4.5:1, non-text elements 3:1. A failing theme is rejected
 * at load in development and fails CI (customer_web theme tests).
 */

export const STATUS_TOKENS = ["success", "warning", "error", "info"] as const;

/** Status tint used behind status text/icons: 12% of the token on background. */
export const STATUS_TINT_AMOUNT = 0.12;

export interface ContrastCheck {
  pair: string;
  ratio: number;
  required: number;
  pass: boolean;
}

export function checkThemeContrast(theme: Pick<TenantTheme, "colors" | "ringOpacity">): ContrastCheck[] {
  const colors = theme.colors as Record<string, string | undefined>;
  const get = (token: string): Rgba => {
    const v = colors[token];
    if (!v) throw new Error(`Theme is missing --${token}`);
    return parseColor(v);
  };
  const bg = composite(get("background"), { r: 1, g: 1, b: 1, a: 1 });
  const checks: ContrastCheck[] = [];
  const add = (pair: string, fg: Rgba, back: Rgba, required: number) => {
    const ratio = contrastRatio(composite(fg, back), back);
    checks.push({ pair, ratio: Math.round(ratio * 100) / 100, required, pass: ratio >= required });
  };

  // Text (4.5:1)
  add("cta-foreground on cta", get("cta-foreground"), composite(get("cta"), bg), 4.5);
  add("primary-foreground on primary", get("primary-foreground"), composite(get("primary"), bg), 4.5);
  add("foreground on background", get("foreground"), bg, 4.5);
  add("muted-foreground on background", get("muted-foreground"), bg, 4.5);
  for (const s of STATUS_TOKENS) {
    const token = composite(get(`status-${s}`), bg);
    const tint = mix(token, bg, STATUS_TINT_AMOUNT);
    add(`status-${s}-foreground on status-${s}`, get(`status-${s}-foreground`), token, 4.5);
    add(`foreground on status-${s} tint`, get("foreground"), tint, 4.5);
    add(`muted-foreground on status-${s} tint`, get("muted-foreground"), tint, 4.5);
    // Non-text (3:1)
    add(`status-${s} on background`, token, bg, 3);
    add(`status-${s} icon on its tint`, token, tint, 3);
  }

  // Non-text (3:1)
  add("input border on background", get("input"), bg, 3);
  add("switch off-track (input) on background", get("input"), bg, 3);
  const ring = get("ring");
  add(
    "ring at --ring-opacity on background",
    { ...ring, a: ring.a * (theme.ringOpacity ?? 1) },
    bg,
    3,
  );
  return checks;
}

export function themeContrastFailures(theme: Pick<TenantTheme, "colors" | "ringOpacity">): ContrastCheck[] {
  return checkThemeContrast(theme).filter((c) => !c.pass);
}
